import { Job_Update, OrderUnitOfMeasure, RateType } from '@treadinc/horizon-api-spec';
import dayjs, { isDayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';
import { extractRateInfoFromJob, resolveJobRateSides } from '~utils/jobs/job-utils';

import { rateTrioDiverges, trioFromSchedule } from '../hooks/useJobScheduleRates';
import { EditSingleJobTableRowDto, InlineJobRateDto } from './editJobTableRowsSchema';

// The inline rate input stores its numeric value as a string (it comes from a
// DOM input), so the seed must be a string too — seeding the raw number would
// leave the field permanently deep-unequal to its default and mark an
// untouched rate dirty, re-PATCHing it on save and polluting Rates History.
function makeInlineJobRateFieldValue(
  rateInfo: { rateType: RateType; rateValue: number | null } | null,
): InlineJobRateDto {
  if (!rateInfo?.rateType) {
    return { rate: '', type: null };
  }

  return {
    rate: isNil(rateInfo.rateValue) ? '' : String(rateInfo.rateValue),
    type: { label: getRateTypeLabel(rateInfo.rateType), value: rateInfo.rateType },
  };
}

export function makeEditJobTableRowDto(job: Job, order: Order) {
  const requestedLoadsCount = (job.loadsCount || job.requestedLoadsCount) ?? null;
  const equipmentTypeGrossCapacity = isNil(job.equipmentTypeGrossCapacity)
    ? null
    : Number(job.equipmentTypeGrossCapacity) || '';
  const jobStartTime = dayjs.tz(job.jobStartAt);

  const { hasCustomerRateSide, hasDriverRateSide, hasVendorRateSide } =
    resolveJobRateSides(job);

  const dto: EditSingleJobTableRowDto = {
    requestedLoadsCount: (requestedLoadsCount ?? '') as number,
    requestedLoadsCountInitialValue: (requestedLoadsCount ?? '') as number,
    equipmentTypeGrossCapacity: (equipmentTypeGrossCapacity ?? '') as number,
    equipmentTypeGrossCapacityInitialValue: (equipmentTypeGrossCapacity ?? '') as number,
    unitOfMeasure: job.unitOfMeasure || null,
    jobQuantity: job.quantity || null,
    jobStartTime: jobStartTime as unknown as Date,
    recurringLoads: Boolean(job.recurringLoads),
    jobNotes: job.jobNotes || '',
    orderUnitOfMeasure: order.unitOfMeasure || null,
    orderQuantity: order.quantity || null,
    material: job.material ?? null,
    customerRate: hasCustomerRateSide
      ? makeInlineJobRateFieldValue(extractRateInfoFromJob(job, 'customer'))
      : null,
    vendorRate: hasVendorRateSide
      ? makeInlineJobRateFieldValue(extractRateInfoFromJob(job, 'pay'))
      : null,
    driverRate: hasDriverRateSide
      ? makeInlineJobRateFieldValue(extractRateInfoFromJob(job, 'driver'))
      : null,
  };

  return dto;
}

/**
 * react-hook-form's `dirtyFields[jobId]` — a nested map of the row's touched
 * leaves. Typed loosely because RHF marks dirt per leaf, at varying depth.
 */
export type DirtySingleJobTableRow = Partial<
  Record<keyof EditSingleJobTableRowDto, unknown>
>;

// A cleared DOM input holds '' — Number('') is 0, which would save a cleared
// rate as $0 instead of clearing the manual rate. Map empty to null explicitly.
function deparseInlineJobRateValue(rawValue: NonNullable<InlineJobRateDto>['rate']) {
  const isEmpty = isNil(rawValue) || String(rawValue).trim() === '';

  return isEmpty ? null : Number(rawValue);
}

function deparseInlineJobRate(
  target: 'customer' | 'vendor' | 'driver',
  rateField: InlineJobRateDto | undefined,
): Partial<Job_Update> {
  return {
    [`${target}_rate_type`]: (rateField?.type?.value as RateType | undefined) ?? null,
    [`${target}_rate_value`]: deparseInlineJobRateValue(rateField?.rate),
  } as Partial<Job_Update>;
}

interface BuildJobUpdateBodyOptions {
  /**
   * Whether this save may clear the job's site-rate-schedule stamp. Mirrors
   * the Edit Job Rates dialog: only when the project rate card flag is on AND
   * the viewer's company owns the order — sub-vendors and drivers down the
   * dispatch chain must never write `site_rate_schedule_id`.
   */
  canClearSiteRateScheduleStamp: boolean;
}

/**
 * Builds the per-job PATCH body for a bulk-edit row. Rate sides and material
 * ride the payload only when their own field is dirty — the BE rejects manual
 * rate edits on invoice-approved sides, so an untouched side must never be
 * sent. A dirty side whose new values diverge from the job's linked rate
 * card clears the `site_rate_schedule_id` stamp, exactly as the Edit Job
 * Rates dialog's divergence watcher does.
 */
export function buildJobUpdateBodyFromRow(
  row: EditSingleJobTableRowDto,
  dirtyRow: DirtySingleJobTableRow,
  job: Job | undefined,
  { canClearSiteRateScheduleStamp }: BuildJobUpdateBodyOptions,
) {
  const {
    equipmentTypeGrossCapacity,
    equipmentTypeGrossCapacityInitialValue,
    jobStartTime,
    jobNotes,
    unitOfMeasure,
    jobQuantity,
  } = row;

  const hasEmptyEquipmentTypeGrossCapacity =
    isNil(equipmentTypeGrossCapacity) || String(equipmentTypeGrossCapacity).trim() === '';

  const hasEmptyEquipmentTypeGrossCapacityInitialValue =
    isNil(equipmentTypeGrossCapacityInitialValue) ||
    String(equipmentTypeGrossCapacityInitialValue).trim() === '';

  // if the user left the "Quantity per Load" field empty, then grab the value
  // set on the job, so we can re-calculate the quantity using that number.
  const effectiveEquipmentTypeGrossCapacity = hasEmptyEquipmentTypeGrossCapacity
    ? hasEmptyEquipmentTypeGrossCapacityInitialValue
      ? undefined
      : Number(equipmentTypeGrossCapacityInitialValue)
    : Number(equipmentTypeGrossCapacity);

  const body: Job_Update = {
    recurring_loads: row.recurringLoads,
    job_start_at:
      jobStartTime && isDayjs(jobStartTime) && jobStartTime.isValid()
        ? jobStartTime.toISOString()
        : undefined,
    notes: jobNotes,
  };

  if (!isNil(effectiveEquipmentTypeGrossCapacity)) {
    body.equipment_type_gross_capacity = effectiveEquipmentTypeGrossCapacity;
  }

  if (unitOfMeasure?.id) {
    body.unit_of_measure = unitOfMeasure.id as OrderUnitOfMeasure;
  }

  if (jobQuantity) {
    body.quantity = isNil(jobQuantity) ? undefined : Number(jobQuantity);
  }

  if (dirtyRow.material) {
    body.material_id = row.material?.id || null;
  }

  const dirtyRateSides = [
    dirtyRow.customerRate
      ? {
          jobSideRateCard: job?.customerJobAssignment?.rate ?? null,
          rateField: row.customerRate,
          target: 'customer' as const,
        }
      : null,
    dirtyRow.vendorRate
      ? {
          jobSideRateCard: job?.vendorJobAssignment?.rate ?? null,
          rateField: row.vendorRate,
          target: 'vendor' as const,
        }
      : null,
    dirtyRow.driverRate
      ? {
          jobSideRateCard: job?.driverRate ?? null,
          rateField: row.driverRate,
          target: 'driver' as const,
        }
      : null,
  ].filter((side) => side !== null);

  dirtyRateSides.forEach((side) => {
    Object.assign(body, deparseInlineJobRate(side.target, side.rateField ?? undefined));

    const linkedSchedule = job?.siteRateSchedule;

    if (canClearSiteRateScheduleStamp && linkedSchedule) {
      const submittedTrio = {
        targetRate: side.jobSideRateCard,
        targetRateType: (side.rateField?.type?.value as RateType | undefined) ?? null,
        targetRateValue: deparseInlineJobRateValue(side.rateField?.rate),
      };

      if (
        rateTrioDiverges(submittedTrio, trioFromSchedule(side.target, linkedSchedule))
      ) {
        body.site_rate_schedule_id = null;
      }
    }
  });

  return body;
}
